import React, { useMemo, useEffect } from 'react';
import { IconWarningRed, ModalClosed } from '~misc/icons';

import useIsMobile from '../hooks/useIsMobile';
import { parseVolume, fmtCurrency, fmtPct, fmtTons, fmtPts, normalizePercentualValue } from '../utils/parseVolume';
import ProgressRail from './ProgressRail';
import Info from '~components/Form/components/Info';
import { atingiuMeta, getDirecaoMetas, getMetaAtingida } from '../utils';

function formatUnidade(value, unit, options = {}) {
    if (value === null || value === undefined) return '-';

    const { minimumFractionDigits = 2, maximumFractionDigits = 2 } = options;
    const numValue = Number(value);

    if (isNaN(numValue)) return '-';

    const formattedValue = numValue.toLocaleString('pt-BR', {
      minimumFractionDigits,
      maximumFractionDigits,
    });

    const normalizedUnit = String(unit || '').toLowerCase().trim();

    switch (normalizedUnit) {
      case 'moeda':
        return `R$ ${formattedValue}`;

      case 'toneladas':
        return `${formattedValue} t`;

      case 'metrocubico':
        return `${formattedValue} m³`;

      case 'percentual': {
        const percentValue = normalizePercentualValue(numValue);

        const isInteger = Math.abs(percentValue - Math.round(percentValue)) < 0.0001;

        return `${percentValue.toLocaleString('pt-BR', {
          minimumFractionDigits: isInteger ? 0 : 2,
          maximumFractionDigits: isInteger ? 0 : 2,
        })}%`;
      }

      case 'unidade': {
        const isInteger = Math.abs(numValue - Math.round(numValue)) < 0.0001;

        return numValue.toLocaleString('pt-BR', {
          minimumFractionDigits: isInteger ? 0 : 2,
          maximumFractionDigits: isInteger ? 0 : 2,
        });
      }

      default:
        return formattedValue;
    }
}

function formatSignedValue(value, formatter) {
  if (!Number.isFinite(value)) return '-';
  const formatted = formatter ? formatter(value) : value.toString();
  if (value > 0) return `+${formatted}`;
  if (value < 0) return formatted;
  return formatted;
}
function getSignedColor(value) {
  if (value > 0) return '#1BB3A7';
  if (value < 0) return '#e42e2ebe';
  return '#9DA2B6';
}

export default function ModalDetalhesIndicadores({ isOpen, onClose, indicador, adicionais, parametroReflex, isHistorico, isMesAtual }) {
  const fmtPctInt = (n) => {
    if (!Number.isFinite(n)) return '-';
    const v = n * 100;
    const opts = { minimumFractionDigits: 0, maximumFractionDigits: 0 };
    return `${v.toLocaleString('pt-BR', opts)}%`;
  };
  const titleCase = (str) => {
    if (typeof str !== 'string') return str;
    return str
      .toLowerCase()
      .split(/\s+/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };
  const isMobile = useIsMobile();
  const modalClasses   = isOpen ? 'fixed inset-0 z-[99999]' : 'hidden';
  const overlayClasses = isOpen ? 'fixed inset-0 bg-black/50 z-40' : 'hidden';
  const direcaoMetas = getDirecaoMetas(indicador?.metas);
  const isDesconto = direcaoMetas === 'descending';
  const helpFatorAjuste = (() => {
    try {
      if (!parametroReflex) return null;

      const targetKpi = String(indicador?.kpi || '').trim().toUpperCase();
      const getTipoStr = (p) => String(p?.tipoKpi || p?.tipoKPI || p?.tipo || '').trim().toUpperCase();
      const isActive = (p) => !('ativo' in p) || Boolean(p.ativo);

      if (Array.isArray(parametroReflex)) {
        const reflexParams = parametroReflex.filter((p) => getTipoStr(p) === 'REFLEX' && isActive(p));
        if (!reflexParams.length) return null;
        const found = reflexParams.find((p) => String(p.kpi || '').trim().toUpperCase() === targetKpi);
        return found?.mensagem ?? null;
      }

      if (!Array.isArray(parametroReflex) && String(parametroReflex.kpi || '').trim().toUpperCase() === targetKpi) {
        if (getTipoStr(parametroReflex) !== 'REFLEX') return null;
        if (!isActive(parametroReflex)) return null;
        return parametroReflex.mensagem ?? null;
      }
      return null;
    } catch (e) {
      return null;
    }
  })();

  const { status, thresholds } = useMemo(() => {
    const metas = indicador?.metas ?? {};
    const m100  = parseVolume(metas.m100);
    const m300  = parseVolume(metas.m300);
    const m500  = parseVolume(metas.m500);
    const m700  = parseVolume(metas.m700);

    const hasM700Asc = Number.isFinite(m700) && m700 > 0;
    const hasM700Desc = Number.isFinite(m700);
    const bonusVolume = hasM700Asc ? m700 : NaN;

    const vols = indicador?.volumes ?? {};
    const volRealAjustadoBase  = parseVolume(vols.realAjustado ?? vols.real);
    const volProjAjustadoBase  = parseVolume(vols.projetadoAjustado);
    const progressBaseVolume   = volRealAjustadoBase;

    const reached = Number.isFinite(progressBaseVolume)
      ? getMetaAtingida({
          valor: progressBaseVolume,
          metas: { m100, m300, m500, m700 },
          includeM700: true,
        })?.toUpperCase() ?? null
      : null;

    const colorClass = (() => {
      if (!Number.isFinite(progressBaseVolume)) return 'text-red-600';
      if (!Number.isFinite(m100)) return 'text-green-600';
      if (reached == null) return 'text-red-600';
      return 'text-green-600';
    })();

    return {
      status: { reached, volume: progressBaseVolume, colorClass },
      thresholds: { m100, m300, m500, m700, bonus: bonusVolume }
    };
  }, [indicador, isDesconto]);


  const showWarning = status.reached == null;

  const precoMeta = parseVolume(indicador?.metaPreco);
  const showPriceFields = Number.isFinite(precoMeta) && precoMeta !== 0;
  const variacaoPrecoPct = parseVolume(indicador?.variacaoPreco);
  const precoReal = parseVolume(indicador?.precoReal);

  const fatorAjustePct    = parseVolume(indicador?.fatorAjuste);
  const impactoPrecoPts     = parseVolume(indicador?.impactoPrecoPonto);

  const variacaoPrecoDisplay = formatSignedValue(variacaoPrecoPct, fmtPct);
  const fatorAjusteDisplay = formatSignedValue(fatorAjustePct, fmtPct);
  const impactoPrecoDisplay = formatSignedValue(impactoPrecoPts, fmtPts);
  const variacaoPrecoColor = getSignedColor(variacaoPrecoPct);
  const fatorAjusteColor = getSignedColor(fatorAjustePct);
  const impactoPrecoColor = getSignedColor(impactoPrecoPts);

  const volumeReal             = parseVolume(indicador?.volumes?.real);
  const volumeRealAjustado     = parseVolume(indicador?.volumes?.realAjustado);
  const volumeProjetadoMes     = parseVolume(indicador?.volumes?.projetado);
  const volumeProjAjustadoMes  = parseVolume(indicador?.volumes?.projetadoAjustado);

  const ratioOk = (() => {
    if (!Number.isFinite(volumeRealAjustado) || !Number.isFinite(volumeProjAjustadoMes)) return false;

    if (isDesconto) {
      if (volumeRealAjustado === 0) return false;
      return Math.abs(volumeProjAjustadoMes / volumeRealAjustado) < 0.9;
    }

    if (volumeProjAjustadoMes === 0) return false;
    return Math.abs(volumeRealAjustado / volumeProjAjustadoMes) < 0.9;
  })();
  const projNaoMenorQueRealAjustado = (Number.isFinite(volumeProjAjustadoMes) && Number.isFinite(volumeRealAjustado))
    ? (isDesconto ? (volumeProjAjustadoMes <= volumeRealAjustado) : (volumeProjAjustadoMes >= volumeRealAjustado))
    : true;
  const hideByM500 = (Number.isFinite(volumeRealAjustado) && Number.isFinite(thresholds?.m500))
    ? atingiuMeta({ valor: volumeRealAjustado, meta: thresholds.m500, direcao: direcaoMetas })
    : false;
  const showProjectedOnRail = ratioOk && !hideByM500 && projNaoMenorQueRealAjustado;

  const atingimentoProjPts     = parseVolume(indicador?.atingimentos?.projetado);
  const atingimentoRealPts     = parseVolume(indicador?.atingimentos?.real);

  const creditoPontosGerados   = parseVolume(indicador?.creditoPontosGerado);
  const creditoPontosRecebidos = parseVolume(indicador?.creditoPontosRecebido);
  const creditoColorGerados = Number.isFinite(creditoPontosGerados) && creditoPontosGerados > 0 ? '#F5C115' : '#9DA2B6';
  const creditoColorRecebidos = Number.isFinite(creditoPontosRecebidos) && creditoPontosRecebidos > 0 ? '#F5C115' : '#F5C115';

  const atingimentoRealDisplay = Number.isFinite(atingimentoRealPts)
    ? fmtPts(atingimentoRealPts)
    : '-';
  const medida = String(indicador?.medida || '').toLowerCase().trim();


  const isDescontoConcreto = String(indicador?.kpi || '').trim().toLowerCase() === 'desconto concreto';
  const volumeContratadoConcreto = parseVolume(indicador?.volumes?.concreto);
  const volumeMinimoConcreto = parseVolume(indicador?.volumes?.concretoGatilho);
  const showVolumeContratadoConcreto = isDescontoConcreto && Number.isFinite(volumeContratadoConcreto);
  const helpVolumeContratadoConcreto = Number.isFinite(volumeMinimoConcreto)
    ? `Volume mínimo: ${formatUnidade(volumeMinimoConcreto, 'metrocubico')}`
    : 'Volume mínimo contratado de concreto.';

  return (
    <div className={modalClasses}>
      <div className="fixed inset-0 z-50 overflow-y-auto overflow-x-hidden">
        <div className={overlayClasses} onClick={onClose} />
        <div className="flex min-h-full items-start md:items-center justify-center p-3 sm:p-4">
          <div className="bg-white px-3 sm:px-4 xl:px-9 py-4 rounded-[10px] shadow-lg z-[60] text-center relative w-full max-w-[calc(100vw-2rem)] sm:max-w-[calc(100vw-3rem)] md:max-w-[85%] lg:max-w-[860px] overflow-visible">
            <div className="flex justify-end">
              <button type="button" onClick={onClose} className="text-gray-500 p-4 rounded-tr-[9px] hover:text-gray-700 bg-quinaria absolute top-0 right-0">
                <ModalClosed />
              </button>
            </div>
            <div>
              <div className="flex items-center justify-start gap-1 md:gap-2">
                <h3 className="font-semibold text-lg sm:text-xl md:text-3xl max-w-[85%] md:max-w-[400px] text-[#01054C] text-left font-poppins leading-tight whitespace-normal break-words md:whitespace-nowrap md:break-normal">
                  {titleCase(indicador?.kpi)}
                </h3>
                {showWarning && (
                  <span className="inline-flex items-center align-middle ml-1 md:ml-2 mb-0.5 text-[1.1em]">
                    <IconWarningRed className="w-10 md:w-auto h-10 md:h-auto" />
                  </span>
                )}
              </div>
              {(!isHistorico || isMesAtual) && (() => {
                const v = status?.volume;
                const m100 = thresholds?.m100;
                const m500 = thresholds?.m500;
                let msg = '';
                let isCreditMsg = false;
                if (Number.isFinite(v) && Number.isFinite(m100) && Number.isFinite(m500)) {
                  if (atingiuMeta({ valor: v, meta: m500, direcao: direcaoMetas })) { msg = 'Você bateu a M500 e pode acumular créditos de pontos!'; isCreditMsg = true; }
                  else if (!atingiuMeta({ valor: v, meta: m100, direcao: direcaoMetas })) msg = 'Você ainda não atingiu volume da M100 e não está pontuando!';
                  else msg = 'Continue acelerando você já atingiu o gatilho!';
                }
                const cls = isCreditMsg
                  ? 'text-green-600'
                  : (msg.includes('não está pontuando') ? 'text-red-600' : 'text-green-600');
                if (isCreditMsg) {
                  return (
                    <p className={`font-semibold text-left text-sm sm:text-base md:text-xl xl:text-2xl font-poppins break-words ${cls}`}>
                      Você bateu a M500 e pode acumular <span className="text-[#F5C115]">créditos de pontos!</span>
                    </p>
                  );
                }
                return (
                  <p className={`font-semibold text-left text-sm sm:text-base md:text-xl xl:text-2xl font-poppins break-words ${cls}`}>
                    {msg}
                  </p>
                );
              })()}
            </div>

            <hr className="mb-4 mt-2 border-t border-[#2020201f]" />

            <ProgressRail
              thresholds={thresholds}
              volume={status.volume}
              reached={!!status.reached}
              isMobile={isMobile}
              isOpen={isOpen}
              projectedVolume={volumeProjAjustadoMes}
              showProjected={showProjectedOnRail}
              descending={isDesconto}
              unit={medida}
              volumeLabel="Volume Real Ajustado"
              projectedLabel="Volume Projetado Ajustado"
            />

            <div className="flex w-full gap-6 flex-col md:flex-row">
              <div className="flex flex-col gap-[6px] flex-1">
                <LabeledRow label="Volume Real:" value={volumeReal} unit={medida} color="#1F61AB" />
                {showPriceFields && (
                  <>
                    <LabeledRow label="Preço Real:" value={precoReal} unit="moeda" color="#1F61AB" />
                    <LabeledRow label="Preço Meta:" value={precoMeta} unit="moeda" color="#1F61AB" />
                    <LabeledRow label="Variação de Preço (Real x Meta):" value={variacaoPrecoDisplay} color={variacaoPrecoColor} />
                    <LabeledRowWithHelp
                      label="Fator de Ajuste:"
                      helpButtonColor="#01054C"
                      helpContent={helpFatorAjuste}
                      value={fatorAjusteDisplay}
                      color={fatorAjusteColor}
                      isMobile={isMobile}
                    />
                    <LabeledRowWithHelp
                      label="Efeito do preço em pontos:"
                      helpButtonColor="#01054C"
                      helpContent={
                        "Impacto do preço real vs preço meta na pontuação desse indicador"
                      }
                      value={impactoPrecoDisplay}
                      color={impactoPrecoColor}
                      isMobile={isMobile}
                    />
                  </>
                )}
                <LabeledRow label="Volume Real Ajustado:" value={volumeRealAjustado} unit={medida} color="#1F61AB" />
                <LabeledRow label="Atingimento Real:" value={atingimentoRealDisplay} color="#389807" />
              </div>

              <div className="flex flex-col gap-[6px] flex-1">
                <LabeledRowWithHelp
                  label="Volume Projetado (mês):"
                  helpButtonColor="#01054C"
                  helpContent={
                    "Estimativa do volume esperado para o mês."
                  }
                  column="right"
                  value={volumeProjetadoMes}
                  unit={medida}
                  color="#9DA2B6"
                  isMobile={isMobile}
                />
                {showPriceFields && (
                  <LabeledRow label="Volume Proj. Ajustado (mês):" value={volumeProjAjustadoMes} unit={medida} color="#9DA2B6" />
                )}
                <LabeledRow label="Atingimento Projetado (mês):" value={fmtPts(atingimentoProjPts)} color="#9DA2B6" />
                {showVolumeContratadoConcreto && (
                  <LabeledRowWithHelp
                    label="Volume Contratado de Concreto (mês):"
                    helpButtonColor="#01054C"
                    helpContent={helpVolumeContratadoConcreto}
                    column="right"
                    value={volumeContratadoConcreto}
                    unit="metrocubico"
                    color="#9DA2B6"
                    isMobile={isMobile}
                  />
                )}
                {creditoPontosGerados !== 0 && (
                  <LabeledRowWithHelp
                    label="Crédito de Pontos Gerados:"
                    helpButtonColor="#01054C"
                    helpContent={
                      "Pontos gerados internamente que somam ao score do indicador."
                    }
                    column="right"
                    value={fmtPts(creditoPontosGerados)}
                    color={creditoColorGerados}
                    isMobile={isMobile}
                  />
                )}
                {creditoPontosRecebidos !== 0 && (
                  <LabeledRowWithHelp
                    label="Crédito de Pontos Recebidos:"
                    helpButtonColor="#01054C"
                    helpContent={
                      "Crédito recebido para maximizar os resultados de atingimento."
                    }
                    column="right"
                    value={fmtPts(creditoPontosRecebidos)}
                    color={creditoColorRecebidos}
                    isMobile={isMobile}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function getValueSizeClass(valueStr) {
  const str = (valueStr ?? '').toString();
  const len = str.length;
  if (len <= 8) return 'text-[16px] sm:text-[16px] md:text-[16px]';
  if (len <= 10) return 'text-[14px] sm:text-[16px] md:text-[16px]';
  if (len <= 11) return 'text-[11px] sm:text-[14px] md:text-[13px]';
  return 'text-[12px] sm:text-[12px] md:text-[12px]';
}

function LabeledRow({ label, value, color, unit, valueIsTons = false, options }) {
  const formatValue = () => {
    if (unit) return formatUnidade(value, unit, options);
    return value;
  };
  const formatted = formatValue();
  const valueSizeClass = getValueSizeClass(formatted);
  return (
    <div className="pl-4 w-full h-[34px] border-y border-l rounded-md flex items-center relative bg-white" style={{ borderColor: color }}>
      <p className="font-poppins text-[13px] md:text-[12px] lg:text-[13px] font-medium w-full text-left text-[#202020]">{label}</p>
      <div className="h-[34px] border-y border-r rounded-r-md -right-[2px] w-[43%] xl:w-[37.73%] relative flex items-center justify-center text-white" style={{ backgroundColor: color, borderColor: color }}>
        <p className={`font-poppins ${valueSizeClass} font-semibold`}>{formatted}</p>
      </div>
    </div>
  );
}

function LabeledRowWithHelp({ label, value, color, helpContent, column = 'left', isMobile, unit, valueIsTons = false, options }) {
  const position = isMobile ? 'bottom-center' : (column === 'left' ? 'right' : 'left');
  const formatValue = () => {
    if (unit) return formatUnidade(value, unit, options);
    return value;
  };
  const formatted = formatValue();
  const valueSizeClass = getValueSizeClass(formatted);
  return (
    <div className="pl-4 w-full h-[34px] border-y border-l rounded-md flex items-center relative bg-white" style={{ borderColor: color }}>
      <div className="flex items-center w-full">
        <p className="font-poppins text-[13px] md:text-[12px] lg:text-[13px] font-medium text-left text-[#202020] whitespace-nowrap pr-2">{label}</p>
          {helpContent && (
            <Info position={position} bg="blue" tooltipClass="w-5 h-5 relative" text={helpContent} />
          )}
      </div>
      <div className="h-[34px] border-y border-r rounded-r-md -right-[2px] w-[43%] xl:w-[37.73%] relative flex items-center justify-center text-white" style={{ backgroundColor: color, borderColor: color }}>
        <p className={`font-poppins ${valueSizeClass} font-semibold`}>{formatted}</p>
      </div>
    </div>
  );
}